import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { FadeInUp, StaggerContainer, StaggerItem, HoverCard } from '../Components/Animations'

// Design tokens
const colors = {
  primary: '#0d9488',
  primaryLight: '#14b8a6',
  primaryDark: '#0f766e',
  secondary: '#0891b2',
  accent: '#5eead4',
  dark: '#0f172a',
  darkBlue: '#1e3a5f',
  text: '#334155',
  textLight: '#64748b',
  white: '#ffffff',
  background: '#f8fafc',
  backgroundAlt: '#f0fdfa'
}

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${colors.white};
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

// Hero Section
const HeroSection = styled.section`
  padding: 160px 0 80px;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(8, 145, 178, 0.15) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 120px 0 60px;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const HeroLabel = styled.span`
  display: inline-block;
  background: rgba(20, 184, 166, 0.2);
  color: ${colors.accent};
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 24px;
`

const HeroTitle = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: ${colors.white};
  margin-bottom: 24px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

// Services Section
const ServicesSection = styled.section`
  padding: 100px 0;
  background: ${colors.background};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 60px;
`

const SectionLabel = styled.span`
  display: inline-block;
  color: ${colors.primary};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 16px;
`

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: ${colors.dark};
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: ${colors.textLight};
  line-height: 1.7;
`

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ServiceCard = styled.div`
  background: ${colors.white};
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.primary}, ${colors.secondary});
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`

const ServiceIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 32px;
`

const ServiceTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 16px;
`

const ServiceDescription = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${colors.textLight};
  margin-bottom: 24px;
  flex-grow: 1;
`

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const ServiceFeature = styled.li`
  font-size: 14px;
  color: ${colors.text};
  margin-bottom: 10px;
  padding-left: 24px;
  position: relative;
  line-height: 1.5;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${colors.primary};
    font-weight: 700;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

// Process Section
const ProcessSection = styled.section`
  padding: 100px 0;
  background: ${colors.white};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const ProcessCard = styled.div`
  text-align: center;
  padding: 30px;
  position: relative;
`

const ProcessNumber = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 24px;
  font-weight: 700;
  color: white;
`

const ProcessTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 12px;
`

const ProcessText = styled.p`
  font-size: 15px;
  color: ${colors.textLight};
  line-height: 1.6;
`

// Technologies Section
const TechSection = styled.section`
  padding: 100px 0;
  background: ${colors.backgroundAlt};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const TechGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`

const TechBadge = styled.div`
  background: ${colors.white};
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    color: ${colors.primary};
  }
`

// CTA Section
const CTASection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 70%, rgba(20, 184, 166, 0.2) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
`

const CTATitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: ${colors.white};
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`

const CTAText = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
  line-height: 1.7;
`

const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  color: white;
  padding: 16px 40px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 40px rgba(20, 184, 166, 0.3);
  }
`

const RSNGServices = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const services = [
    {
      icon: '💻',
      title: 'Product Development',
      description: 'From initial concept to final deployment, we build scalable, robust software solutions that perfectly fit your vision.',
      features: [
        'Custom software development',
        'Full-stack application development',
        'Scalable architecture design',
        'End-to-end product lifecycle'
      ]
    },
    {
      icon: '☁️',
      title: 'Cloud Solutions',
      description: 'Implementing Kubernetes, auto-scaling infrastructure, and strategic approaches that maximize performance.',
      features: [
        'Kubernetes implementation',
        'Auto-scaling infrastructure',
        'Cloud cost optimization',
        'Performance monitoring'
      ]
    },
    {
      icon: '🔄',
      title: 'CI/CD Pipelines',
      description: 'Automating development pipelines to accelerate delivery timelines and improve software quality.',
      features: [
        'CI/CD pipeline setup',
        'Automated testing integration',
        'Deployment automation',
        'Release management'
      ]
    },
    {
      icon: '🎨',
      title: 'UI/UX Design',
      description: 'Creating intuitive, beautiful interfaces that enhance user engagement and deliver exceptional experiences.',
      features: [
        'User experience research',
        'Interface design & prototyping',
        'Responsive design',
        'Usability testing'
      ]
    },
    {
      icon: '👥',
      title: 'Staff Augmentation',
      description: 'Flexible offshore talent solutions to scale your team efficiently with specialized expertise.',
      features: [
        'Dedicated offshore teams',
        'Flexible engagement models',
        'Specialized skill sets',
        'Seamless team integration'
      ]
    },
    {
      icon: '📊',
      title: 'Data Science',
      description: 'Leveraging advanced analytics and data management to drive smarter business decisions.',
      features: [
        'Advanced analytics',
        'Data pipeline development',
        'Machine learning solutions',
        'Business intelligence'
      ]
    },
    {
      icon: '✅',
      title: 'Quality Assurance',
      description: 'Ensuring robust, error-free software releases through comprehensive testing strategies.',
      features: [
        'Automated testing frameworks',
        'Manual QA testing',
        'Performance testing',
        'Security audits'
      ]
    },
    {
      icon: '🤖',
      title: 'AI & ML Solutions',
      description: 'Harnessing artificial intelligence to automate processes and create intelligent solutions.',
      features: [
        'Custom AI model development',
        'Natural language processing',
        'Computer vision solutions',
        'Predictive analytics'
      ]
    },
    {
      icon: '⚡',
      title: 'Salesforce Solutions',
      description: 'Comprehensive Salesforce consulting, implementation, and customization to optimize your CRM.',
      features: [
        'Salesforce implementation',
        'Custom development',
        'Integration services',
        'Support & optimization'
      ]
    }
  ]

  const processSteps = [
    { number: '01', title: 'Discovery', text: 'Understanding your goals, challenges, and requirements' },
    { number: '02', title: 'Strategy', text: 'Crafting a tailored roadmap for your project' },
    { number: '03', title: 'Development', text: 'Building your solution with agile methodology' },
    { number: '04', title: 'Delivery', text: 'Launching and supporting your product' }
  ]

  const technologies = [
    'React', 'Node.js', 'Python', 'AWS', 'Azure', 'Kubernetes', 'Docker',
    'PostgreSQL', 'MongoDB', 'TensorFlow', 'Salesforce', 'TypeScript',
    'GraphQL', 'Redis', 'Elasticsearch', 'Terraform', 'Jenkins', 'Git'
  ]

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <FadeInUp>
              <HeroLabel>Our Services</HeroLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <HeroTitle>Expert Solutions for Digital Transformation</HeroTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <HeroSubtitle>
                Transform ideas into reality with creative digital solutions, tailored strategies, and future-ready technology. We empower businesses to thrive in a digital-first world.
              </HeroSubtitle>
            </FadeInUp>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      {/* Services Section */}
      <ServicesSection>
        <MaxWidthContainer>
          <SectionHeader>
            <FadeInUp>
              <SectionLabel>What We Offer</SectionLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <SectionTitle>Comprehensive Technology Services</SectionTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <SectionSubtitle>
                From product development to cloud solutions, we provide end-to-end services to accelerate your digital journey.
              </SectionSubtitle>
            </FadeInUp>
          </SectionHeader>

          <StaggerContainer staggerDelay={0.1}>
            <ServicesGrid>
              {services.map((service, index) => (
                <StaggerItem key={index}>
                  <HoverCard scale={1.02}>
                    <ServiceCard>
                      <ServiceIcon>{service.icon}</ServiceIcon>
                      <ServiceTitle>{service.title}</ServiceTitle>
                      <ServiceDescription>{service.description}</ServiceDescription>
                      <ServiceFeatures>
                        {service.features.map((feature, idx) => (
                          <ServiceFeature key={idx}>{feature}</ServiceFeature>
                        ))}
                      </ServiceFeatures>
                    </ServiceCard>
                  </HoverCard>
                </StaggerItem>
              ))}
            </ServicesGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </ServicesSection>

      {/* Process Section */}
      <ProcessSection>
        <MaxWidthContainer>
          <SectionHeader>
            <FadeInUp>
              <SectionLabel>Our Approach</SectionLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <SectionTitle>How We Work</SectionTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <SectionSubtitle>
                A proven process that ensures quality, transparency, and successful outcomes for every project.
              </SectionSubtitle>
            </FadeInUp>
          </SectionHeader>

          <StaggerContainer staggerDelay={0.15}>
            <ProcessGrid>
              {processSteps.map((step, index) => (
                <StaggerItem key={index}>
                  <ProcessCard>
                    <ProcessNumber>{step.number}</ProcessNumber>
                    <ProcessTitle>{step.title}</ProcessTitle>
                    <ProcessText>{step.text}</ProcessText>
                  </ProcessCard>
                </StaggerItem>
              ))}
            </ProcessGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </ProcessSection>

      {/* CTA Section */}
      <CTASection>
        <MaxWidthContainer>
          <CTAContent>
            <FadeInUp>
              <CTATitle>Ready to Transform Your Business?</CTATitle>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <CTAText>
                Let's discuss how our services can help you achieve your goals and drive innovation in your organization.
              </CTAText>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <CTAButton onClick={() => navigate('/contact')}>
                Get Started Today
                <span>→</span>
              </CTAButton>
            </FadeInUp>
          </CTAContent>
        </MaxWidthContainer>
      </CTASection>
    </Container>
  )
}

export default RSNGServices
